import { Group, Text, useMantineTheme } from '@mantine/core';
import { Dropzone, DropzoneProps, FileWithPath, MIME_TYPES } from '@mantine/dropzone';
import { IconCloudUpload, IconDownload, IconX } from '@tabler/icons-react';
import { useRef } from 'react';

import { CONTROL_DOCUMENT_ALLOWED_MIME_TYPES } from '@e-origin/shared';
import { UPLOADFILE_THEME_COLOR_INDEX } from '@constants';

const MIME_TYPE_MAP = {
  pdf: [MIME_TYPES.pdf],
  image: [MIME_TYPES.jpeg, MIME_TYPES.png],
  csv: [MIME_TYPES.csv],
  xlsx: [MIME_TYPES.xlsx, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  docx: [MIME_TYPES.docx, 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  zip: [MIME_TYPES.zip],
  json: ['application/json'],
  '7z': [MIME_TYPES['7z']],
  documents: [...CONTROL_DOCUMENT_ALLOWED_MIME_TYPES],
  all: [],
};

type UploadFileBaseProps = {
  onBlur?: () => void;
  error?: React.ReactNode;
  type?: keyof typeof MIME_TYPE_MAP;
  disabled?: boolean;
  maxSize?: number;
  description?: string;
} & Omit<DropzoneProps, 'children' | 'accept' | 'maxSize' | 'disabled' | 'onDrop'>;

type SingleFileProps = UploadFileBaseProps & {
  isMulti: false;
  onChange?: (file: File) => void;
  value?: File;
};

type MultiFileProps = UploadFileBaseProps & {
  isMulti: true;
  onChange?: (files: File[]) => void;
  value?: File[];
};

type UploadFileProps = SingleFileProps | MultiFileProps;

const convertToFile = (fileWithPath: FileWithPath): File => {
  return new File([fileWithPath], fileWithPath.name, {
    type: fileWithPath.type,
    lastModified: fileWithPath.lastModified,
  });
};

export const UploadFile: React.FC<UploadFileProps> = (props) => {
  const { onChange, onBlur, value, error, type = 'pdf', disabled = false, isMulti, maxSize, description, ...dropzoneProps } = props;

  const theme = useMantineTheme();
  const openRef = useRef<() => void>(null);

  const acceptedTypes = MIME_TYPE_MAP[type] ?? [];

  const defaultDescription = `Drag 'n' drop ${type === 'all' ? 'any' : type} files, or click to select files`;

  const handleDrop = (files: FileWithPath[]) => {
    if (onChange) {
      if (isMulti === false) {
        (onChange as (file: FileWithPath) => void)(convertToFile(files[0]));
      } else {
        (onChange as (files: FileWithPath[]) => void)(files.map(convertToFile));
      }
    }
    if (onBlur) {
      onBlur();
    }
  };

  const hasError = !!error;

  const displaySelectedFiles = () => {
    if (!value) return null;

    if (Array.isArray(value)) {
      return <Text c="blue">Selected: {value.map((file) => file.name).join(', ')}</Text>;
    } else {
      return <Text c="blue">Selected: {value.name}</Text>;
    }
  };

  return (
    <div>
      <Dropzone
        openRef={openRef}
        onDrop={handleDrop}
        accept={acceptedTypes.length ? acceptedTypes : undefined}
        maxSize={maxSize}
        disabled={disabled}
        multiple={isMulti !== false}
        inputProps={{ 'data-pw': 'upload-file-input' } as any}
        {...dropzoneProps}
      >
        <Group align="center" style={{ pointerEvents: 'none' }}>
          <Dropzone.Accept>
            <IconDownload size={50} color={theme.colors.blue[UPLOADFILE_THEME_COLOR_INDEX]} stroke={1.5} />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX size={50} color={theme.colors.red[UPLOADFILE_THEME_COLOR_INDEX]} stroke={1.5} />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconCloudUpload
              size={50}
              stroke={1.5}
              color={hasError ? theme.colors.red[UPLOADFILE_THEME_COLOR_INDEX] : theme.colors.blue[UPLOADFILE_THEME_COLOR_INDEX]}
            />
          </Dropzone.Idle>

          <div>
            <Text c={hasError ? 'red' : 'dimmed'}>{description ?? defaultDescription}</Text>
            {hasError && <Text c="red">{error}</Text>}
            {value && displaySelectedFiles()}
          </div>
        </Group>
      </Dropzone>
    </div>
  );
};
